/**
 * Drag and Drop Feature
 * Validates drop targets for file tree drag operations
 */

import { ItemInstance, DragTarget } from "@headless-tree/core";
import { Datum } from "../datastructure";
import { isReadOnlyDatum } from "../specialFolders";

export interface CanDropOptions {
  /** Whether in across-drives mode */
  isAcrossDrivesMode: boolean;
  /** Current drive ID (for single-drive mode) */
  driveId?: string;
  /** This org's read-only client drive names, served per-org (#10972). */
  readOnlyDriveNames?: readonly string[];
  /** This org's named SharePoint site roots, served per-org (#11713). */
  sharepointRootNames?: readonly string[];
}

/**
 * Creates a canDrop validator for the file tree.
 * Prevents:
 * - Cross-drive moves
 * - Dropping on synthetic root in across-drives mode
 * - Dropping a folder into itself or its descendants
 */
export function createCanDropValidator({
  isAcrossDrivesMode,
  driveId,
  readOnlyDriveNames,
  sharepointRootNames,
}: CanDropOptions): (
  items: ItemInstance<Datum>[],
  target: DragTarget<Datum>
) => boolean {
  const readOnlyCtx = {
    readOnlyDriveNames,
    sharepointRootNames,
    isAcrossDrivesMode,
  };
  return (items: ItemInstance<Datum>[], target: DragTarget<Datum>): boolean => {
    const targetDatum = target.item.getItemData();

    // Only allow dropping onto folders (not files)
    if (targetDatum.type !== "folder") {
      return false;
    }

    // In across-drives mode, don't allow dropping on the synthetic root
    // (the root only contains synthetic drive folders, not real file locations)
    if (isAcrossDrivesMode && targetDatum.path.length === 0) {
      return false;
    }

    // Prevent dropping into read-only special folders
    if (isReadOnlyDatum(targetDatum, readOnlyCtx)) {
      return false;
    }

    // Prevent dragging from read-only special folders
    for (const item of items) {
      if (isReadOnlyDatum(item.getItemData(), readOnlyCtx)) {
        return false;
      }
    }

    // In single-drive mode, root (path []) represents the drive itself,
    // so use the driveId prop. Otherwise use the target's payload driveId.
    const targetDriveId =
      !isAcrossDrivesMode && targetDatum.path.length === 0
        ? (driveId ?? null)
        : (targetDatum.payload?.driveId ?? null);

    // Check all dragged items are from the same drive as the target
    for (const item of items) {
      const itemDatum = item.getItemData();
      const itemDriveId = itemDatum.payload?.driveId ?? null;

      // Prevent cross-drive moves
      if (itemDriveId !== targetDriveId) {
        return false;
      }

      // Don't allow dropping a folder into itself or its descendants
      if (itemDatum.type === "folder") {
        const targetPath = targetDatum.path;
        const itemPath = itemDatum.path;
        // Check if target path starts with item path (would be dropping into self/descendant)
        if (
          targetPath.length >= itemPath.length &&
          itemPath.every((seg, i) => targetPath[i] === seg)
        ) {
          return false;
        }
      }
    }

    return true;
  };
}
